import { useState } from "react";
import { Link } from "react-router";
import SimpleTable from "../../../shared/SimpleTable";
import { fetchJvByRestLink } from "../../../../utils/apiUtils";
import { jvClientQueryString } from "../../../javelina/clientQuery";
import StopLossContractDetail, { asJvArray } from "./StopLossContractDetail";

const TABS = [
    { id: 'locations', label: 'Locations' },
    { id: 'coverages', label: 'Coverages' },
    { id: 'stop-loss', label: 'Stop Loss' },
];

const tabBarStyle = {
    display: 'flex',
    gap: '.4rem',
    flexWrap: 'wrap',
    borderBottom: '1px solid var(--lightGrey)',
    margin: '1.2rem 0',
};

const tabButtonStyle = (active) => ({
    border: '1px solid var(--lightGrey)',
    borderBottom: active ? '1px solid var(--white)' : '1px solid var(--lightGrey)',
    background: active ? 'var(--white)' : 'var(--lightestGrey)',
    cursor: 'pointer',
    padding: '.5rem 1rem',
    fontSize: '.95rem',
    fontWeight: active ? 600 : 500,
    color: active ? 'var(--darkerGrey)' : 'var(--grey)',
    borderRadius: '.4rem .4rem 0 0',
    marginBottom: '-1px',
});

const DepartmentDetail = ({ department, locationName, onBack }) => {
    const addresses = asJvArray(department?.ClientDepartmentAddress);
    const phones = asJvArray(department?.ClientDepartmentPhoneNum);

    return (
        <div>
            <p className="clickable-link" style={{ paddingBottom: '1rem' }} onClick={onBack}>
                ← {locationName || 'Location'}
            </p>
            <p className="sub-title">{department?.DepartmentName}</p>
            <p className="title">{department?.Description || department?.DepartmentName}</p>
            <p>BlkOfBusID: {department?.BlkOfBusID}</p>
            <p>ClientID: {department?.ClientID}</p>
            <p>ContactName: {department?.ContactName}</p>
            <p>Email: {department?.Email}</p>
            <p>Status: {department?.Status}</p>
            <p>StopLossContract: {department?.StopLossContract}</p>
            <p>CoverageInEffectSinceDate: {department?.CoverageInEffectSinceDate}</p>
            {department?.Comments && <p>Comments: {department.Comments}</p>}

            {!!addresses.length && (
                <>
                    <p style={{ fontWeight: 600, fontSize: '1rem', padding: '1rem 0 .5rem' }}>Addresses</p>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'EffectiveDate', attribute: 'EffectiveDate' },
                            { label: 'Address1', attribute: 'Address1' },
                            { label: 'City', attribute: 'City' },
                            { label: 'State', attribute: 'State' },
                            { label: 'PostalCode', attribute: 'PostalCode' },
                        ]}
                        rows={addresses}
                    />
                </>
            )}

            {!!phones.length && phones.map((phone, i) => (
                <div key={`dept-phone-${i}`} style={{ paddingTop: '1rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '1rem', paddingBottom: '.5rem' }}>Phone</p>
                    <p>Work: {phone.WorkPhone1}{phone.WorkPhone1Ext ? ` x${phone.WorkPhone1Ext}` : ''}</p>
                    <p>Fax: {phone.FaxPhone1}</p>
                    <p>Mobile: {phone.MobilePhone1}</p>
                </div>
            ))}
        </div>
    );
};

const LocationDetail = ({ location, onBack }) => {
    const addresses = asJvArray(location?.ClientLocationAddress);
    const phones = asJvArray(location?.ClientLocationPhoneNum);
    const departments = asJvArray(location?.ClientLocationDepartment);
    const [department, setDepartment] = useState(null);
    const [loadingDept, setLoadingDept] = useState(null);
    const [deptError, setDeptError] = useState(null);

    const openDepartment = async (dept) => {
        if (!dept?.DepartmentLink) return;
        try {
            setLoadingDept(dept.DepartmentName);
            setDeptError(null);
            const result = await fetchJvByRestLink(dept.DepartmentLink);
            const record = asJvArray(result?.Response?.ClientDepartment)[0] || dept;
            setDepartment(record);
        } catch (err) {
            setDeptError(err.message);
        } finally {
            setLoadingDept(null);
        }
    };

    if (department) {
        return (
            <DepartmentDetail
                department={department}
                locationName={location?.LocationName}
                onBack={() => setDepartment(null)}
            />
        );
    }

    return (
        <div>
            <p className="clickable-link" style={{ paddingBottom: '1rem' }} onClick={onBack}>← Locations</p>
            <p className="sub-title">{location?.LocationName}</p>
            <p className="title">{location?.Description || location?.LocationName}</p>
            <p>BlkOfBusID: {location?.BlkOfBusID}</p>
            <p>ClientID: {location?.ClientID}</p>
            <p>ContactName: {location?.ContactName}</p>
            <p>Email: {location?.Email}</p>
            <p>Status: {location?.Status}</p>
            <p>StopLossContract: {location?.StopLossContract}</p>
            <p>VendorName: {location?.VendorName}</p>
            <p>Area: {location?.Area}</p>
            <p>CoverageInEffectSinceDate: {location?.CoverageInEffectSinceDate}</p>
            {location?.Comments && <p>Comments: {location.Comments}</p>}

            {!!addresses.length && (
                <>
                    <p style={{ fontWeight: 600, fontSize: '1rem', padding: '1rem 0 .5rem' }}>Addresses</p>
                    <SimpleTable
                        disableHeaderFormatting={true}
                        columns={[
                            { label: 'EffectiveDate', attribute: 'EffectiveDate' },
                            { label: 'Address1', attribute: 'Address1' },
                            { label: 'City', attribute: 'City' },
                            { label: 'State', attribute: 'State' },
                            { label: 'PostalCode', attribute: 'PostalCode' },
                        ]}
                        rows={addresses}
                    />
                </>
            )}

            {!!phones.length && phones.map((phone, i) => (
                <div key={`phone-${i}`} style={{ paddingTop: '1rem' }}>
                    <p style={{ fontWeight: 600, fontSize: '1rem', paddingBottom: '.5rem' }}>Phone</p>
                    <p>Work: {phone.WorkPhone1}{phone.WorkPhone1Ext ? ` x${phone.WorkPhone1Ext}` : ''}</p>
                    <p>Fax: {phone.FaxPhone1}</p>
                    <p>Mobile: {phone.MobilePhone1}</p>
                </div>
            ))}

            {!!departments.length && (
                <>
                    <p style={{ fontWeight: 600, fontSize: '1rem', padding: '1rem 0 .5rem' }}>Departments</p>
                    {deptError && <p className="danger" style={{ paddingBottom: '.5rem' }}>{deptError}</p>}
                    {departments.map((dept, i) => (
                        <div key={`${dept?.DepartmentName || i}-dept`} style={{ paddingBottom: '.6rem' }}>
                            {dept?.DepartmentLink ? (
                                <p
                                    className="clickable-link"
                                    onClick={() => openDepartment(dept)}
                                >
                                    {loadingDept === dept.DepartmentName ? 'Loading...' : dept.DepartmentName}
                                </p>
                            ) : (
                                <p>{dept?.DepartmentName}</p>
                            )}
                        </div>
                    ))}
                </>
            )}
        </div>
    );
};

const Locations = ({ client }) => {
    const locations = asJvArray(client?.ClientLocation);
    const [detail, setDetail] = useState(null);
    const [loadingName, setLoadingName] = useState(null);
    const [error, setError] = useState(null);

    const openLocation = async (loc) => {
        if (!loc?.LocationLink) return;
        try {
            setLoadingName(loc.LocationName);
            setError(null);
            const result = await fetchJvByRestLink(loc.LocationLink);
            const location = asJvArray(result?.Response?.ClientLocation)[0] || loc;
            setDetail(location);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingName(null);
        }
    };

    if (detail) {
        return <LocationDetail location={detail} onBack={() => setDetail(null)} />;
    }

    if (!locations.length) {
        return <p style={{ color: 'var(--grey)' }}>No locations on this client.</p>;
    }

    return (
        <div>
            {error && <p className="danger" style={{ paddingBottom: '1rem' }}>{error}</p>}
            {locations.map((loc, i) => (
                <div key={`${loc?.LocationName || i}-location`} style={{ paddingBottom: '1rem' }}>
                    <p>
                        LocationName:{' '}
                        {loc?.LocationLink ? (
                            <span
                                className="clickable-link"
                                onClick={() => openLocation(loc)}
                            >
                                {loadingName === loc.LocationName ? 'Loading...' : loc.LocationName}
                            </span>
                        ) : loc?.LocationName}
                    </p>
                    <p>Description: {loc?.Description}</p>
                </div>
            ))}
        </div>
    );
};

const Coverages = ({ client }) => {
    const coverages = asJvArray(client?.ClientCoverage)
        .slice()
        .sort((a, b) => String(b?.EffectiveDate || '').localeCompare(String(a?.EffectiveDate || '')));

    if (!coverages.length) {
        return <p style={{ color: 'var(--grey)' }}>No coverages on this client.</p>;
    }

    return coverages.map((cov, i) => (
        <div key={`${cov?.LocationName || i}-${cov?.EffectiveDate || i}`} style={{ padding: '1rem 0rem', borderTop: '1px solid var(--lightGrey)' }}>
            <p>LocationName: {cov?.LocationName}</p>
            <p>EffectiveDate: {cov?.EffectiveDate}</p>
            <p>TerminationDate: {cov?.TerminationDate}</p>
            <p>TerminationReason: {cov?.TerminationReason}</p>

            <p style={{ fontWeight: 600, fontSize: '1rem', padding: '1rem 0rem' }}>Client Primary Coverage</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {asJvArray(cov?.ClientPrimaryCoverage)
                    .filter((pcov) => pcov && pcov.ClientPrimaryCoverageBenefitPlan)
                    .map((pcov, j) => (
                        <div key={`${pcov?.PrimaryCoverageID || j}-pcov`} style={{ padding: '0rem 0rem 1rem 0rem' }}>
                            <p>PrimaryCoverageID: {pcov?.PrimaryCoverageID}</p>
                            <p style={{ padding: '0rem 0rem 0.6rem 0rem' }}>EffectiveDate: {pcov?.EffectiveDate}</p>
                            <SimpleTable
                                columns={[{ label: 'BenefitPlanID', attribute: 'BenefitPlanID' }]}
                                disableHeaderFormatting={true}
                                rows={asJvArray(pcov.ClientPrimaryCoverageBenefitPlan)}
                            />
                        </div>
                    ))}
            </div>
        </div>
    ));
};

const Client = ({ data }) => {
    const clients = asJvArray(data?.details?.Response?.Client);
    const client = clients[0] || {};
    const stopLoss = data?.details?.stopLoss;
    const [tab, setTab] = useState('locations');

    const titleName = client?.ClientName || data?.clientname || data?.ClientName;
    const titleId = client?.ClientID || data?.clientid || data?.ClientID;
    const blkOfBusId = client?.BlkOfBusID || data?.blkofbusid || data?.BlkOfBusID;
    const relatedQuery = jvClientQueryString({
        clientId: titleId,
        blkOfBusId,
        clientName: titleName,
    });

    const tabCounts = {
        locations: asJvArray(client?.ClientLocation).length,
        coverages: asJvArray(client?.ClientCoverage).length,
        'stop-loss': stopLoss?.contracts?.length || (client?.StopLossContract ? 1 : 0),
    };

    return (
        <div>
            <p className="sub-title">{titleId}</p>
            <p className="title">{titleName}</p>
            {titleId && (
                <p style={{ display: 'flex', gap: '1.2rem', paddingTop: '.35rem' }}>
                    <Link className="clickable-link" to={`/jv/employees?${relatedQuery}`}>Employees</Link>
                    <Link className="clickable-link" to={`/jv/reinsurance?${relatedQuery}`}>Reinsurance</Link>
                </p>
            )}
            {client?.StopLossContract && (
                <p className="sub-title">Assigned stop-loss contract: {client.StopLossContract}</p>
            )}

            <div role="tablist" style={tabBarStyle}>
                {TABS.map((item) => {
                    const active = tab === item.id;
                    const count = tabCounts[item.id];
                    return (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => setTab(item.id)}
                            style={tabButtonStyle(active)}
                        >
                            {item.label}
                            {count ? ` (${count})` : ''}
                        </button>
                    );
                })}
            </div>

            {tab === 'locations' && <Locations client={client} />}
            {tab === 'coverages' && <Coverages client={client} />}
            {tab === 'stop-loss' && (
                <StopLossContractDetail stopLoss={stopLoss} assignedName={client?.StopLossContract} />
            )}
        </div>
    );
};

export default Client;
